import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { MIDWIFERY_PROMO } from '../config/featureFlags';

interface SubscriptionGateProps {
  children: React.ReactNode;
  featureName?: string;
  freeContent?: React.ReactNode;
}

export default function SubscriptionGate({ children, featureName = 'this feature', freeContent }: SubscriptionGateProps) {
  const { isAuthenticated, isSubscribed, isLoading, login, track } = useAuth();
  const navigate = useNavigate();
  const isMidwifery = track === 'midwifery';
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  const handleMidwiferyCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan: 'midwifery-90days' }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to start checkout');
      }
      const data = await res.json();
      window.location.href = data.checkoutUrl;
    } catch (err: any) {
      setCheckoutError(err.message || 'Something went wrong. Please try again.');
      setCheckoutLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    // If free content is provided, show that for non-authenticated users too
    if (freeContent) {
      return <>{freeContent}</>;
    }
    
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <LockIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              Login Required
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Please log in to access {featureName}.
            </Typography>
            <Button variant="contained" size="large" onClick={login}>
              Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!isSubscribed) {
    // If free content is provided, show that instead of the paywall
    if (freeContent) {
      return <>{freeContent}</>;
    }
    
    if (isMidwifery) {
      return (
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <LockIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Premium Content
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Subscribe to unlock {featureName} and get full access to the complete Gulf midwifery review.
              </Typography>
              {checkoutError && (
                <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }} onClose={() => setCheckoutError('')}>
                  {checkoutError}
                </Alert>
              )}
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  What's included:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
                  <li>{MIDWIFERY_PROMO.studyModuleCount} In-Depth Study Modules</li>
                  <li>{MIDWIFERY_PROMO.questionCount} Practice Questions with Rationales</li>
                  <li>Timed Mock Exams ({MIDWIFERY_PROMO.examShortList})</li>
                  <li>Progress Tracking</li>
                  <li>{MIDWIFERY_PROMO.alignmentLabel}</li>
                </Typography>
              </Alert>
              <Button
                variant="contained"
                size="large"
                disabled={checkoutLoading}
                onClick={handleMidwiferyCheckout}
                sx={{ mb: 1 }}
              >
                {checkoutLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Renew Midwife Access'}
              </Button>
              <Typography variant="caption" display="block" color="text.secondary">
                90-day full access
              </Typography>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <LockIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              Premium Content
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Subscribe to unlock {featureName} and get full access to all study materials, practice tests, and exam simulations.
            </Typography>
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                What's included:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
                <li>Comprehensive Study Handbook</li>
                <li>100+ Practice Questions</li>
                <li>Timed Exam Simulations</li>
                <li>Progress Tracking</li>
                <li>Mobile-Friendly Access</li>
              </Typography>
            </Alert>
            <Button 
              variant="contained" 
              size="large" 
              onClick={() => navigate('/subscribe')}
              sx={{ mb: 1 }}
            >
              View Subscription Plans
            </Button>
            <Typography variant="caption" display="block" color="text.secondary">
              Plans start at ₱999 for 30 days
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return <>{children}</>;
}
